/**
 * @file: utils.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { MaintenanceServicePlanDetailType } from '@euler/model/enum';
import {
  MaintenancePlan,
  MaintenancePlanDetail,
} from '@euler/model/task-detail';
import {
  INSURANCE_EXPIRY_ITEM_NAME,
  MaintenancePlanListItem,
  MaintenancePlanListItemActionInfo,
  MaintenancePlanListItemBase,
  MaintenancePlanListItemInfo,
} from './types';

export const isInspectionSubjectMaintenancePlanItem = (
  item: MaintenancePlanListItem,
): item is Extract<MaintenancePlanListItem, { type: 'inspection-subject' }> => {
  return item.type === 'inspection-subject';
};

export const isConstructionJobMaintenancePlanItem = (
  item: MaintenancePlanListItem,
): item is Extract<MaintenancePlanListItem, { type: 'construction-job' }> => {
  return item.type === 'construction-job';
};

export const isPendingIssueMaintenancePlanItem = (
  item: MaintenancePlanListItem,
): item is Extract<MaintenancePlanListItem, { type: 'pending-issue' }> => {
  return item.type === 'pending-issue';
};

export const isFaultLikeSubjectMaintenancePlanItem = (
  item: MaintenancePlanListItem,
): item is Extract<MaintenancePlanListItem, { type: 'fault' }> => {
  return item.type === 'fault';
};

export const isCustomMaintenancePlanItem = (
  item: MaintenancePlanListItem,
): item is Extract<MaintenancePlanListItem, { type: 'custom' }> => {
  return item.type === 'custom';
};

export const isInsuranceExpiryMaintenancePlanItem = (
  item: MaintenancePlanListItem,
): item is Extract<MaintenancePlanListItem, { type: 'insurance-expiry' }> => {
  return item.type === 'insurance-expiry';
};

export const maintenancePlanListItemFromPlanDetail = (
  plan: MaintenancePlan,
  detail: MaintenancePlanDetail,
): MaintenancePlanListItem | undefined => {
  const baseItem: MaintenancePlanListItemBase = {
    id: `__existing_plan_detail:${plan.id}:${detail.id}__`,
    name: detail.name,
    planInfo: {
      nextServiceDate: detail.nextServiceDate,
      nextServiceMileage: detail.nextServiceMileage,
      remark: detail.remark,
    },
    existing: { plan, detail },
  };

  let item: MaintenancePlanListItem | undefined = undefined;
  switch (detail.type) {
    case MaintenanceServicePlanDetailType.ConstructionJob:
      item = {
        type: 'construction-job',
        ...baseItem,
        jobId: 0, // not used
        author: '', // not used
      };
      break;
    case MaintenanceServicePlanDetailType.InspectionSubject:
      item = {
        type: 'inspection-subject',
        ...baseItem,
        flowId: '',
        author: '',
      };
      break;
    case MaintenanceServicePlanDetailType.FaultLikeSubject:
      item = { type: 'fault', ...baseItem, faultInspectionItem: {} as any };
      break;
    case MaintenanceServicePlanDetailType.PendingIssue:
    case MaintenanceServicePlanDetailType.SolvedPendingIssue:
      item = {
        type: 'pending-issue',
        ...baseItem,
        issue: {} as any,
        repairConfirmed:
          detail.type === MaintenanceServicePlanDetailType.SolvedPendingIssue,
      };
      break;
    case MaintenanceServicePlanDetailType.Custom:
      item = { type: 'custom', ...baseItem };
      break;
    case MaintenanceServicePlanDetailType.InsuranceExpiry:
      item = {
        type: 'insurance-expiry',
        ...baseItem,
        name: INSURANCE_EXPIRY_ITEM_NAME,
        optedIn: true,
      };
      break;
  }

  return item;
};

export const maintenancePlanListItemInfoFromItem = (
  item: MaintenancePlanListItem,
): MaintenancePlanListItemInfo => {
  return {
    name:
      item.type === 'insurance-expiry' ? INSURANCE_EXPIRY_ITEM_NAME : item.name,

    get planDetailType(): MaintenanceServicePlanDetailType {
      switch (item.type) {
        case 'inspection-subject':
          return MaintenanceServicePlanDetailType.InspectionSubject;
        case 'construction-job':
          return MaintenanceServicePlanDetailType.ConstructionJob;
        case 'pending-issue':
          return item.repairConfirmed
            ? MaintenanceServicePlanDetailType.SolvedPendingIssue
            : MaintenanceServicePlanDetailType.PendingIssue;
        case 'fault':
          return MaintenanceServicePlanDetailType.FaultLikeSubject;
        case 'custom':
          return MaintenanceServicePlanDetailType.Custom;
        case 'insurance-expiry':
          return MaintenanceServicePlanDetailType.InsuranceExpiry;
      }
    },

    get subTitle() {
      switch (item.type) {
        case 'inspection-subject':
          return `[车辆检测] 检测技师: ${item.author}`;
        case 'construction-job':
          return `[施工反馈] 施工技师: ${item.author}`;
        case 'pending-issue':
          return item.repairConfirmed
            ? '[遗留问题] 已确认维修'
            : '[遗留问题] 未确认维修';
        case 'fault':
          return '';
        case 'custom':
          return '自定义保养提醒';
        case 'insurance-expiry':
          return '';
      }
    },

    get action(): MaintenancePlanListItemActionInfo {
      switch (item.type) {
        case 'inspection-subject':
        case 'construction-job':
          return {
            actionType: 'view-detail',
            type: 'link',
            title: '查看报告',
            textColor: '#777',
          };
        case 'pending-issue':
          return {
            actionType: item.repairConfirmed
              ? 'reset-as-pending-issue'
              : 'confirm-repair',
            type: 'button',
            title: item.repairConfirmed ? '确认遗留' : '确认维修',
            textColor: item.repairConfirmed
              ? 'rgba(64, 161, 199, 1)'
              : 'rgb(64, 199, 121)',
            backgroundColor: item.repairConfirmed
              ? 'rgba(64, 161, 199, 0.2)'
              : 'rgba(64, 199, 121, 0.2)',
          };
        case 'fault':
          return {
            actionType: item.solved
              ? 'reset-fault-like-subject-unsolved'
              : 'confirm-solve-fault-like-subject',
            type: 'button',
            title: '已解决',
            textColor: item.solved ? '#fff' : '#ccc',
            borderColor: item.solved ? 'rgb(64, 199, 121)' : '#ccc',
            backgroundColor: item.solved ? 'rgb(64, 199, 121)' : 'transparent',
            icon: 'check',
            iconColor: item.solved ? '#fff' : '#ccc',
            iconSize: 14,
          };
        case 'custom':
          return {
            actionType: 'remove',
            type: 'button',
            title: '移除计划',
            textColor: '#FF3B30',
            backgroundColor: 'rgba(255, 59, 48, 0.2)',
          };
        case 'insurance-expiry':
          return item.optedIn
            ? {
                actionType: 'opt-out-insurance-expiry',
                type: 'button',
                title: '移除提醒',
                textColor: '#FF3B30',
                backgroundColor: 'rgba(255, 59, 48, 0.2)',
              }
            : {
                actionType: 'opt-in-insurance-expiry',
                type: 'button',
                title: '加入提醒',
                textColor: 'rgb(64, 199, 121)',
                backgroundColor: 'rgba(64, 199, 121, 0.2)',
              };
      }
    },
  };
};
